import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CuentaConectada from '../integraciones/CuentaConectada';
import { doc, getDoc, collection, query, where, limit, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { formatDate } from '../../utils/dateHelpers';
import { useOrganization } from '../../context/OrganizationContext';
import MeetingTypeConfig from './MeetingTypeConfig';
import AvailabilityConfig from './AvailabilityConfig';
import AppointmentTemplatesPanel from '../templates/AppointmentTemplatesPanel';
import { DENTALINK_HABILITADO } from '../../config/featureFlags';
import ListaDeAgendas from './ListaDeAgendas';

const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL ||
  "https://us-central1-sistema-bks.cloudfunctions.net";

/**
 * Componente: Configuración de Recordatorios Automáticos
 */
function BookingRemindersConfig({ config, onUpdate }) {
  const { t } = useTranslation('bots');
  const [enabled, setEnabled] = useState(true);
  const [channels, setChannels] = useState({ whatsapp: true, email: false });
  const [intervals, setIntervals] = useState(['1d']);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config?.calendar?.reminders) {
      const r = config.calendar.reminders;
      setEnabled(r.enabled ?? true);
      setChannels(r.channels || { whatsapp: true, email: false });
      setIntervals(r.intervals || ['1d']);
    }
  }, [config]);

  const handleIntervalToggle = (interval) => {
    if (intervals.includes(interval)) {
      setIntervals(intervals.filter(i => i !== interval));
    } else {
      if (intervals.length >= 3) {
        alert(t('integrations.calendar.maxReminders'));
        return;
      }
      setIntervals([...intervals, interval]);
    }
  };

  const handleSave = async () => {
    if (intervals.length === 0) {
      alert(t('integrations.calendar.selectOneInterval'));
      return;
    }

    if (!channels.whatsapp && !channels.email) {
      alert(t('integrations.calendar.selectOneChannel'));
      return;
    }

    setSaving(true);

    try {
      await onUpdate({
        calendar: {
          ...config.calendar,
          reminders: {
            enabled,
            channels,
            intervals
          }
        }
      });
      alert(t('integrations.calendar.remindersSaved'));
    } catch (error) {
      console.error('Error saving reminders:', error);
      alert(t('integrations.calendar.remindersSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const INTERVAL_OPTIONS = [
    { value: '2d', label: t('integrations.calendar.interval2d') },
    { value: '1d', label: t('integrations.calendar.interval1d') },
    { value: '12h', label: t('integrations.calendar.interval12h') },
    { value: '6h', label: t('integrations.calendar.interval6h') },
    { value: '3h', label: t('integrations.calendar.interval3h') }
  ];

  return (
    <div style={{
      background: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      padding: '1.5rem',
      marginTop: '1rem'
    }}>
      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
        {t('integrations.calendar.remindersTitle')}
      </h3>
      <p style={{ color: '#718096', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
        {t('integrations.calendar.remindersDesc')}
      </p>

      {/* Toggle principal */}
      <div style={{ margin: '1rem 0', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            style={{ width: '18px', height: '18px' }}
          />
          <span>{t('integrations.calendar.enableReminders')}</span>
        </label>
      </div>

      {enabled && (
        <>
          {/* Selección de canales */}
          <div style={{ margin: '1.5rem 0' }}>
            <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem', fontWeight: 600 }}>
              {t('integrations.calendar.sendChannels')}
            </h4>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={channels.whatsapp}
                  onChange={(e) => setChannels({ ...channels, whatsapp: e.target.checked })}
                />
                <span>📱 WhatsApp</span>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={channels.email}
                  onChange={(e) => setChannels({ ...channels, email: e.target.checked })}
                />
                <span>📧 Email</span>
              </label>
            </div>
          </div>

          {/* Selección de intervalos */}
          <div style={{ margin: '1.5rem 0' }}>
            <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem', fontWeight: 600 }}>
              {t('integrations.calendar.whenToSend')}
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '0.75rem'
            }}>
              {INTERVAL_OPTIONS.map(opt => (
                <div
                  key={opt.value}
                  onClick={() => handleIntervalToggle(opt.value)}
                  style={{
                    position: 'relative',
                    padding: '1rem',
                    border: `2px solid ${intervals.includes(opt.value) ? '#48bb78' : '#e2e8f0'}`,
                    background: intervals.includes(opt.value) ? '#f0fff4' : 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center'
                  }}
                >
                  <span style={{ fontSize: '0.9rem' }}>{opt.label}</span>
                  {intervals.includes(opt.value) && (
                    <span style={{
                      position: 'absolute',
                      top: '0.25rem',
                      right: '0.25rem',
                      color: '#48bb78',
                      fontWeight: 'bold'
                    }}>✓</span>
                  )}
                </div>
              ))}
            </div>
            <p style={{ fontSize: '0.85rem', color: '#718096', margin: '0.5rem 0 0 0' }}>
              {t('integrations.calendar.remindersSelected', { count: intervals.length })}
            </p>
          </div>

          <button
            onClick={handleSave}
            disabled={saving || intervals.length === 0}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: saving || intervals.length === 0 ? '#cbd5e0' : '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 600,
              cursor: saving || intervals.length === 0 ? 'not-allowed' : 'pointer',
              marginTop: '1rem'
            }}
          >
            {saving ? t('integrations.saving') : t('integrations.saveConfig')}
          </button>
        </>
      )}
    </div>
  );
}

export default function CalendarIntegration({ user, botId, config, onUpdate, onRecargar, checkIntegrationSlot }) {
  const { t } = useTranslation('bots');
  const { context } = useOrganization();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [botData, setBotData] = useState(null);
  const [playbookData, setPlaybookData] = useState(null);

  // Dentalink no es OAuth: es un formulario con token estático, así que necesita su
  // propio estado local (Google/Microsoft sólo redirigen y no guardan nada acá).
  const [showDentalinkForm, setShowDentalinkForm] = useState(false);
  const [dentalinkSaving, setDentalinkSaving] = useState(false);
  const [dentalinkError, setDentalinkError] = useState(null);
  const [dentalinkForm, setDentalinkForm] = useState({
    apiToken: '', sucursalId: '', profesionalId: '', estadoAnuladoId: '',
  });
  // Sólo se puebla al guardar: la URL lleva el token de webhook embebido y por eso NO
  // se persiste en Firestore ni se puede releer desde el front. Volver a guardar la
  // conexión la muestra de nuevo SIN rotar el token (el backend lo reutiliza), así que
  // la URL ya pegada en el panel de Dentalink sigue siendo válida.
  const [webhookUrl, setWebhookUrl] = useState(null);

  // Load bot and playbook data for MeetingTypeConfig
  useEffect(() => {
    if (!user || !botId) return;

    const loadBotData = async () => {
      try {
        // Use organization-aware paths
        const botsPath = context.mode === 'organization' && context.orgId
          ? ['organizations', context.orgId, 'bots']
          : ['clientes', user.uid, 'bots'];

        const botRef = doc(db, ...botsPath, botId);
        const botSnap = await getDoc(botRef);

        if (botSnap.exists()) {
          const data = botSnap.data();
          setBotData(data);

          // Load active playbook
          const activePlaybookId = data.runtime?.activePlaybookId;
          if (activePlaybookId) {
            const playbookRef = doc(db, ...botsPath, botId, 'playbooks', activePlaybookId);
            const playbookSnap = await getDoc(playbookRef);

            if (playbookSnap.exists()) {
              setPlaybookData({ id: activePlaybookId, ...playbookSnap.data() });
            }
          }
          // Fallback: query any active playbook if no activePlaybookId
          if (!activePlaybookId) {
            const playbooksRef = collection(db, ...botsPath, botId, 'playbooks');
            const q = query(playbooksRef, where('active', '==', true), limit(1));
            const snap = await getDocs(q);
            if (!snap.empty) {
              setPlaybookData({ id: snap.docs[0].id, ...snap.docs[0].data() });
            }
          }
        }
      } catch (error) {
        console.error('Error loading bot data:', error);
      }
    };

    loadBotData();
  }, [user, botId, context]);

  // ⛔ `handleConnect` SE RETIRÓ EN LA FASE 3f (8-sep-2026), no se comentó.
  //
  // Era el «conectar Google / conectar Microsoft» del render binario: el dueño conectaba SU
  // calendario al bot en un acto suelto. Con el modelo de lista eso ya no existe — conectar
  // es siempre conectar UNA agenda, y pasa por su enlace o por el flujo del dueño sobre una
  // agenda concreta. Al desaparecer los botones, la función se quedó sin llamantes.
  //
  // Se BORRA en vez de dejarla «por si acaso»: una función viva que nadie llama es la forma
  // más silenciosa de que alguien la vuelva a cablear mañana sin ver que compone la
  // conexión con la forma anterior (`calendar.provider`, sin agenda).
  //
  // ⚠️ Y ESTA NOTA DECÍA UNA FALSEDAD, corregida el 8-sep-2026 al preparar el deploy.
  // Afirmaba «`startCalendarOAuth` sigue existiendo: lo usa el flujo del dueño», y ese
  // flujo NO existía: al borrar los botones, el único camino para conectar quedó siendo el
  // enlace de invitación, así que el dueño tenía que auto-enviarse un link y confirmarse a
  // sí mismo. Lo destapó el `grep` del deploy — la única referencia a `startCalendarOAuth`
  // en `src/` era este propio comentario.
  //
  // Ahora sí hay dos caminos, los dos en `ListaDeAgendas.jsx`: «Conectar mi calendario»
  // (endpoint `conectarAgendaPropia`, queda conectada) e «Invitar a alguien» (enlace,
  // queda por confirmar). `startCalendarOAuth` sigue desplegado y lo usa el primero, por
  // debajo de `conectarAgendaPropia`.

  const handleSaveDentalink = async () => {
    // Mismo gate comercial que Google/Microsoft: Dentalink es un proveedor de
    // CALENDARIO, así que consume el mismo slot en plan Starter.
    if (checkIntegrationSlot) {
      const slotCheck = checkIntegrationSlot('calendar');
      if (!slotCheck.allowed) {
        alert(slotCheck.reason);
        return;
      }
    }

    if (!dentalinkForm.apiToken.trim()) {
      setDentalinkError('Pegá el token de API de Dentalink.');
      return;
    }

    setDentalinkSaving(true);
    setDentalinkError(null);

    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`${API_URL}/saveDentalinkConfig`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          organizationId: context.mode === 'organization' && context.orgId ? context.orgId : null,
          botId,
          config: {
            apiToken: dentalinkForm.apiToken.trim(),
            sucursalId: dentalinkForm.sucursalId.trim() || null,
            profesionalId: dentalinkForm.profesionalId.trim() || null,
            estadoAnuladoId: dentalinkForm.estadoAnuladoId.trim()
              ? Number(dentalinkForm.estadoAnuladoId.trim())
              : null,
          },
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        // El backend manda un mensaje accionable (dónde sacar el token, qué revisar):
        // se muestra tal cual en vez de aplanarlo a "error al guardar".
        setDentalinkError(data.error || 'No se pudo conectar con Dentalink.');
        return;
      }

      setWebhookUrl(data.webhookUrl || null);
      setShowDentalinkForm(false);
      // El token no se conserva en memoria del navegador más de lo necesario.
      setDentalinkForm({ apiToken: '', sucursalId: '', profesionalId: '', estadoAnuladoId: '' });
      onUpdate({ calendar: { provider: 'dentalink', connected: true } });
    } catch (error) {
      console.error('Error conectando Dentalink:', error);
      setDentalinkError('No se pudo contactar el servidor. Intentá de nuevo.');
    } finally {
      setDentalinkSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t('integrations.calendar.confirmDisconnect'))) {
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`${API_URL}/disconnectCalendar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          uid: user.uid,
          organizationId: context.mode === 'organization' && context.orgId ? context.orgId : null,
          botId, // per-bot calendar: disconnect only this bot's connection
        }),
      });

      const data = await res.json();
      if (data.success) {
        onUpdate({ calendar: { provider: null } });
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
      alert(t('integrations.disconnectError'));
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`${API_URL}/testCalendarConnection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ uid: user.uid }),
      });

      const data = await res.json();
      setTestResult(data);
    } catch (error) {
      console.error('Error testing:', error);
      setTestResult({ success: false, error: t('integrations.connectionError') });
    } finally {
      setTesting(false);
    }
  };

  // FASE 3a/3f — `calendar.provider` ya no existe: bajó a cada entrada de `calendar.agendas`.
  // Dejarlo así habría hecho que TODO lo de abajo (recordatorios, disponibilidad, tipo de
  // reunión) desapareciera para bots que sí tienen calendario, sin un solo error visible.
  //
  // Y sólo cuenta `conectada`: una agenda en `por_confirmar` no la usa el bot, así que
  // enseñar su configuración de agendamiento sugeriría que ya está operando.
  const agendasDelBot = Array.isArray(config?.calendar?.agendas) ? config.calendar.agendas : [];
  const isConnected = agendasDelBot.some((a) => a?.estado === 'conectada');

  return (
    <div className="integration-card">
      <div className="integration-header">
        <div className="integration-icon">📅</div>
        <div>
          <h3>{t('integrations.calendar.title')}</h3>
          <p className="integration-description">
            {t('integrations.calendar.description')}
          </p>
        </div>
      </div>

      {/* FASE 3f — LA LISTA SUSTITUYE AL RENDER BINARIO.
          Antes esto era `{!isConnected ? <botones de proveedor> : <cuenta conectada>}`, que
          asumía UNA conexión por bot. Con N agendas el binario no tiene nada que decir: no
          hay «el» proveedor ni «la» cuenta. Los botones de Google/Microsoft desaparecen de
          aquí porque conectar ya no es un acto suelto — es conectar UNA agenda, y eso pasa
          por el enlace de su tarjeta. */}
      <ListaDeAgendas config={config} botId={botId} onRecargar={onRecargar} />

      {/* Dentalink sigue APAGADO (Fase 2) y sigue siendo del bot entero, no de una agenda:
          por la regla de composición, o hay N agendas OAuth o hay UNA de Dentalink. El
          bloque se movió aquí sin tocarle una línea; lo único añadido son los dos `<div>`
          de apertura que antes le daba el ternario. */}
      <div className="connection-options">
        <div className="provider-buttons">
            {/* Dentalink NO es OAuth: abre un formulario en vez de redirigir.
                APAGADO (Fase 2, 8-sep-2026): el backend no está desplegado, así que el
                botón NO SE RENDERIZA — no es `display:none`, no existe en el DOM. Ver
                `src/config/featureFlags.js` para los cuatro sitios que hay que encender
                a la vez. */}
            {DENTALINK_HABILITADO && (
              <button
                onClick={() => { setShowDentalinkForm(v => !v); setDentalinkError(null); }}
                className="provider-btn dentalink"
              >
                🦷 {t('integrations.calendar.connectDentalink', 'Conectar Dentalink')}
              </button>
            )}
          </div>

          {DENTALINK_HABILITADO && showDentalinkForm && (
            <div className="dentalink-form" style={{ marginTop: '1rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              <p style={{ fontSize: '0.875rem', color: '#4a5568', marginTop: 0 }}>
                El token lo genera el administrador de la clínica en el panel de
                Dentalink: <strong>Configuración → API</strong>.
              </p>

              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginTop: '0.75rem' }}>
                Token de API <span style={{ color: '#e53e3e' }}>*</span>
              </label>
              <input
                type="password"
                autoComplete="off"
                value={dentalinkForm.apiToken}
                onChange={(e) => setDentalinkForm({ ...dentalinkForm, apiToken: e.target.value })}
                placeholder="Pegá el token de Dentalink"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #cbd5e0', borderRadius: '4px' }}
              />

              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginTop: '0.75rem' }}>
                ID de sucursal
              </label>
              <input
                type="text"
                value={dentalinkForm.sucursalId}
                onChange={(e) => setDentalinkForm({ ...dentalinkForm, sucursalId: e.target.value })}
                placeholder="Ej: 1"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #cbd5e0', borderRadius: '4px' }}
              />
              {/* No es cosmético: la sucursal es la clave del lock anti-doble-reserva
                  (resolveCalendarKey). Sin ella el lock se degrada. */}
              <p style={{ fontSize: '0.78rem', color: '#718096', margin: '0.25rem 0 0 0' }}>
                Recomendado: sin sucursal se pierde la protección contra reservas
                simultáneas del mismo horario.
              </p>

              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginTop: '0.75rem' }}>
                ID de profesional (opcional)
              </label>
              <input
                type="text"
                value={dentalinkForm.profesionalId}
                onChange={(e) => setDentalinkForm({ ...dentalinkForm, profesionalId: e.target.value })}
                placeholder="Dejar vacío para toda la sucursal"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #cbd5e0', borderRadius: '4px' }}
              />

              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginTop: '0.75rem' }}>
                ID del estado "Anulada" (opcional)
              </label>
              <input
                type="text"
                value={dentalinkForm.estadoAnuladoId}
                onChange={(e) => setDentalinkForm({ ...dentalinkForm, estadoAnuladoId: e.target.value })}
                placeholder="Ej: 5"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #cbd5e0', borderRadius: '4px' }}
              />
              <p style={{ fontSize: '0.78rem', color: '#718096', margin: '0.25rem 0 0 0' }}>
                Sin este dato el bot no puede anular citas — las derivará a una persona.
                Cada cuenta de Dentalink usa su propio número.
              </p>

              {dentalinkError && (
                <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: '#fff5f5', color: '#c53030', borderRadius: '4px', fontSize: '0.85rem' }}>
                  {dentalinkError}
                </div>
              )}

              <button
                onClick={handleSaveDentalink}
                disabled={dentalinkSaving}
                style={{
                  width: '100%', marginTop: '1rem', padding: '0.75rem',
                  background: dentalinkSaving ? '#cbd5e0' : '#667eea', color: 'white',
                  border: 'none', borderRadius: '6px', fontWeight: 600,
                  cursor: dentalinkSaving ? 'not-allowed' : 'pointer',
                }}
              >
                {dentalinkSaving ? 'Verificando token…' : 'Conectar Dentalink'}
              </button>
            </div>
          )}
        </div>

      {/* Probar y desconectar operan sobre el bot entero, no sobre una agenda:
          `testCalendarConnection` monta el adapter por defecto y `disconnectCalendar` vacía
          la lista. Quitar UNA agenda es el botón de su propia tarjeta. */}
      {isConnected && (
        <div className="connected-section">
          <div className="actions">
            <button
              onClick={handleTest}
              disabled={testing}
              className="btn-test"
            >
              {testing ? t('integrations.testing') : t('integrations.testConnection')}
            </button>
            <button onClick={handleDisconnect} className="btn-disconnect">
              {t('integrations.disconnect')}
            </button>
          </div>

          {testResult && (
            <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
              {testResult.success
                ? t('integrations.connectionSuccess')
                : `${t('integrations.connectionFailed')}: ${testResult.error}`}
            </div>
          )}
        </div>
      )}

      {isConnected && (
        <>
          {/* Meeting Type Configuration - BEFORE Reminders */}
          <MeetingTypeConfig
            playbook={playbookData || {}}
            botId={botId}
            activePlaybookId={botData?.runtime?.activePlaybookId || playbookData?.id}
            user={user}
          />

          {/* Availability Configuration */}
          <AvailabilityConfig
            schedulingConfig={config?.calendar?.schedulingConfig}
            onChange={async ({ schedulingConfig: sc }) => {
              // Per-bot vía onUpdate del panel — el MISMO camino que reminders
              // más arriba en este archivo. (Antes: updateDoc directo al doc
              // {owner}/integrations/config, el fósil del refactor per-bot de
              // junio: lo guardado no lo leía ni el runtime —que usa el doc
              // per-bot sin fallback— ni esta misma pantalla al recargar.
              // Cuarta víctima del patrón dos-fuentes-de-verdad, 04-ago.)
              await onUpdate({
                calendar: {
                  ...config.calendar,
                  schedulingConfig: sc
                }
              });
            }}
          />

          {/* Booking Reminders Configuration */}
          <BookingRemindersConfig
            config={config}
            onUpdate={onUpdate}
          />

          {/* WhatsApp appointment_reminder templates status (only for Cloud API bots) */}
          <AppointmentTemplatesPanel
            botId={botId}
            numeroId={botData?.numeroId || null}
            organizationId={context?.mode === 'organization' ? context?.orgId : null}
            uid={user?.uid}
          />
        </>
      )}

      <style>{`
        .integration-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 1rem;
        }

        .integration-header {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .integration-icon {
          font-size: 2.5rem;
        }

        .integration-card h3 {
          margin: 0 0 0.25rem 0;
          color: #2d3748;
          font-size: 1.25rem;
        }

        .integration-description {
          color: #718096;
          font-size: 0.875rem;
          margin: 0;
        }

        .connection-options {
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .help-text {
          color: #4a5568;
          margin-bottom: 1rem;
          font-size: 0.9rem;
        }

        .provider-buttons {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .provider-btn {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1.25rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 500;
          transition: all 0.2s;
        }

        .provider-btn:hover {
          border-color: #667eea;
          background: #f7fafc;
        }

        .provider-btn.google:hover {
          border-color: #4285f4;
        }

        .provider-btn.microsoft:hover {
          border-color: #00a4ef;
        }

        .provider-icon {
          width: 20px;
          height: 20px;
        }

        .info-box {
          background: #fff5e5;
          border: 1px solid #ffd580;
          border-radius: 6px;
          padding: 1rem;
          color: #8b5a00;
          font-size: 0.875rem;
        }

        .connected-section {
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .status-badge {
          display: inline-block;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          font-weight: 600;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }

        .status-badge.connected {
          background: #c6f6d5;
          color: #22543d;
        }

        .connection-meta {
          font-size: 0.875rem;
          color: #718096;
          margin: 0.5rem 0;
        }

        .connection-meta code {
          background: #f7fafc;
          padding: 0.125rem 0.375rem;
          border-radius: 3px;
          font-family: monospace;
          font-size: 0.85em;
        }

        .actions {
          display: flex;
          gap: 0.75rem;
          margin-top: 1.5rem;
          flex-wrap: wrap;
        }

        .btn-test,
        .btn-disconnect {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-test {
          background: #667eea;
          color: white;
        }

        .btn-test:hover:not(:disabled) {
          background: #5568d3;
        }

        .btn-test:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-disconnect {
          background: white;
          color: #e53e3e;
          border: 1px solid #fc8181;
        }

        .btn-disconnect:hover {
          background: #fff5f5;
        }

        .test-result {
          margin-top: 1rem;
          padding: 0.75rem;
          border-radius: 6px;
          font-size: 0.875rem;
        }

        .test-result.success {
          background: #f0fff4;
          color: #22543d;
          border: 1px solid #9ae6b4;
        }

        .test-result.error {
          background: #fff5f5;
          color: #742a2a;
          border: 1px solid #fc8181;
        }
      `}</style>
    </div>
  );
}
